import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Checkbox, Form, Icon } from "semantic-ui-react";
import { format } from "date-fns";
import { visibleFindings } from "./piModel";
import { Card, LinkOut } from "./PiEvidence";

const DEFAULT_PERSONAL_NOTE = "Initial AI research about this home. Please review as part of your due diligence.";

// A new draft starts from the server-cleaned generated summary (research
// failures and tool errors removed) and the default personal note; a saved
// draft keeps its text as-is, so a note the agent cleared stays cleared.
// Sections added since the draft was saved (e.g. Sale and Permit History)
// are inserted in their generated position.
function initialDraft(clientSummary, cleanedGenerated) {
  const saved = clientSummary?.draft;
  const generated = cleanedGenerated || clientSummary?.generated || { sections: [] };
  const base = saved || generated;
  let sections = (base.sections || []).map((s) => ({ ...s }));
  if (saved) {
    const have = new Set(sections.map((s) => s.key));
    (generated.sections || []).forEach((g, i) => {
      if (have.has(g.key)) return;
      const prev = (generated.sections || []).slice(0, i).reverse().find((s) => have.has(s.key));
      const at = prev ? sections.findIndex((s) => s.key === prev.key) + 1 : 0;
      sections.splice(at, 0, { ...g });
      have.add(g.key);
    });
  }
  return {
    sections,
    personalNote: saved ? saved.personalNote || "" : DEFAULT_PERSONAL_NOTE,
    recipientPartyIds: base.recipientPartyIds || [],
  };
}

const comparable = (d) => JSON.stringify({ s: d?.sections?.map(({ key, title, body, include }) => ({ key, title, body, include })), n: d?.personalNote || "" });

/**
 * Agent-editable, client-facing summary built from the saved report. Saving
 * and approving only store text - nothing here re-researches the property.
 */
export default function PiClientSummaryEditor({ p, report, clientParties, onSave, onShare }) {
  const summary = report.clientSummary || {};
  const [draft, setDraft] = useState(() => initialDraft(summary, p.clientSummaryGenerated));
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const hiddenMaterial = useMemo(
    () => visibleFindings(report).filter((f) => f.edit?.hiddenFromClient && ["potential_risk", "conflicting_records"].includes(f.status)),
    [report]
  );
  const approved = summary.approved;
  const changedSinceApproval = approved && comparable(draft) !== comparable(approved);
  const included = draft.sections.filter((s) => s.include && s.body.trim());

  if (!summary.generated) return <p className="pi-empty">The client summary draft will be ready when research finishes.</p>;

  const updateSection = (key, patch) => setDraft((d) => ({ ...d, sections: d.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));
  const toggleRecipient = (id) => setDraft((d) => ({ ...d, recipientPartyIds: d.recipientPartyIds.includes(id) ? d.recipientPartyIds.filter((x) => x !== id) : [...d.recipientPartyIds, id] }));

  async function save(approve) {
    setSaving(true);
    try {
      await onSave({ draft, approve });
      toast.success(approve ? "Client summary approved." : "Draft saved.");
    } catch (error) {
      toast.error(error?.message || "Unable to save the client summary.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pi-grid-2">
      <div className="pi-col">
        <div className="pi-note">
          <Icon name="eye" />
          <div>This is the <strong>shareable client version</strong>. Your full research report, diagnostics and notes stay internal.</div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div className="pi-section-heading">{preview ? "Client preview" : "Edit summary"}</div>
          <Button.Group size="small">
            <Button active={!preview} onClick={() => setPreview(false)} content="Edit" />
            <Button active={preview} onClick={() => setPreview(true)} content="Preview" />
          </Button.Group>
        </div>

        {preview ? (
          <div className="pi-card" style={{ padding: "24px 28px" }}>
            {draft.personalNote && <p className="pi-p" style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 14px", fontStyle: "italic", whiteSpace: "pre-wrap" }}>{draft.personalNote}</p>}
            {included.length === 0 && <p className="pi-empty">No sections are included yet.</p>}
            {included.map((s) => (
              <div key={s.key} style={{ marginBottom: 18 }}>
                <h4 style={{ color: "#16243c", margin: "0 0 6px", fontSize: 17 }}>{s.title}</h4>
                {s.body.split(/\n\s*\n/).map((para, i) => <p key={i} className="pi-p">{para}</p>)}
              </div>
            ))}
            {p.officialLinks.length > 0 && (
              <div style={{ borderTop: "1px solid #eef1f5", paddingTop: 12 }}>
                <div className="pi-label" style={{ marginBottom: 6 }}>Helpful official resources</div>
                {p.officialLinks.map((l) => <div key={l.url} style={{ marginBottom: 4 }}><LinkOut link={l} /></div>)}
              </div>
            )}
          </div>
        ) : (
          <Form>
            <div className="pi-card">
              <Form.TextArea
                label="Personal note to your client (optional)"
                rows={3}
                value={draft.personalNote}
                onChange={(e, { value }) => setDraft((d) => ({ ...d, personalNote: value }))}
                placeholder="A few words in your own voice - it appears at the top of the summary."
              />
            </div>
            {draft.sections.map((s) => (
              <div key={s.key} className="pi-card" style={{ marginTop: 12, opacity: s.include ? 1 : 0.65 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <Checkbox toggle checked={s.include} onChange={(e, { checked }) => updateSection(s.key, { include: checked })} aria-label={`Include ${s.title}`} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input type="text" style={{ width: "100%", fontWeight: 600 }} value={s.title} onChange={(e) => updateSection(s.key, { title: e.target.value })} aria-label="Section title" />
                  </div>
                </div>
                {s.include && <Form.TextArea rows={Math.min(14, Math.max(4, Math.ceil(s.body.length / 85) + (s.body.match(/\n\s*\n/g) || []).length))} value={s.body} onChange={(e, { value }) => updateSection(s.key, { body: value })} aria-label={`${s.title} text`} />}
              </div>
            ))}
          </Form>
        )}
      </div>

      <div className="pi-col">
        <Card title="Approval & Sharing" icon="check circle outline">
          {approved ? (
            <p className="pi-small" style={{ marginTop: -4 }}>
              <Icon name="check circle" color="green" />
              Version {approved.version} approved {approved.approvedAt ? format(new Date(approved.approvedAt), "MMM d, yyyy h:mm a") : ""}.
              {changedSinceApproval && <strong style={{ color: "#b45309", display: "block", marginTop: 4 }}>You have changes that aren&apos;t approved yet.</strong>}
            </p>
          ) : (
            <p className="pi-small pi-muted" style={{ marginTop: -4 }}>Review the sections, then approve the summary. Only an approved version can be shared or turned into a PDF.</p>
          )}
          {hiddenMaterial.length > 0 && (
            <div className="pi-note" style={{ background: "#fff8e6", borderColor: "#f3e0a8", color: "#6b4e00", marginBottom: 12 }}>
              <Icon name="warning sign" style={{ color: "#a16207" }} />
              <div>
                {hiddenMaterial.length} potential-risk or conflicting finding(s) are hidden from the client: {hiddenMaterial.map((f) => f.title).join("; ")}. Make sure the summary still discloses material concerns.
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button primary loading={saving} disabled={saving || included.length === 0 || (approved && !changedSinceApproval)} onClick={() => save(true)} icon="check" content={approved ? "Approve Changes" : "Approve Summary"} />
            <Button basic loading={saving} disabled={saving} onClick={() => save(false)} content="Save Draft" />
            <Button color="blue" disabled={!approved || changedSinceApproval} onClick={() => onShare(draft.recipientPartyIds)} icon="share" content="Share with Client" />
          </div>
          <p className="pi-small pi-muted" style={{ margin: "10px 0 0" }}>Sharing lets you create the branded PDF, post it to the client portal and/or prepare an email. Nothing is sent automatically.</p>
        </Card>

        <Card title="Recipients" icon="users">
          {clientParties.length === 0 ? <p className="pi-empty">No client with an email address is on this transaction yet.</p> : (
            clientParties.map((party) => (
              <div key={party.id} style={{ padding: "6px 0" }}>
                <Checkbox
                  label={`${party.firstName || ""} ${party.lastName || ""} (${party.role})`.trim()}
                  checked={draft.recipientPartyIds.includes(party.id)}
                  onChange={() => toggleRecipient(party.id)}
                />
                <div className="pi-small pi-muted" style={{ paddingLeft: 26, overflowWrap: "anywhere" }}>{party.email}</div>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}
